import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import {
    Alert, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, Link as MuiLink, MenuItem, Paper, Switch, Table, TableBody, TableCell,
    TableContainer, TableFooter, TableHead, TextField, Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import StripedTableRow from "../../components/StripedTableRow";
import TableFooterRow from "../../components/TableFooterRow";
import TableHeaderRow from "../../components/TableHeaderRow";
import MultiSelect from "../../components/multiSelect/multiSelect";
import SingleSelect from "../../components/SingleSelect";
import downloadCsv from "../../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";
import {useAuth} from "../../providers/auth";
import {PermissionType} from "../../objects/UserRead";
import {searchPubs} from "../blockedPublishers/blockedPublishersService";
import {expandExportDataColumns, LinkoutPresetSnapshot, normalizeConversionStatPreset, toIntIds, uniqueOrderedByIds, yearBeforeMonthOrder} from "./conversionStatPreset";
import {
    applyMultiSort,
    ConversionStatSortLabel,
    parseSortStack,
    SortEntry,
    sortStackForPreset,
} from "./conversionStatSort";

const DEFAULT_SORT_STACK: SortEntry[] = [{column: "leads", direction: "desc"}];

const DATA_OPTIONS = [
    {id: "broker", value: "Broker"},
    {id: "school", value: "School"},
    {id: "publisher", value: "Publisher"},
    {id: "publisher_id", value: "Publisher ID"},
    {id: "when_start", value: "When Start"},
    {id: "portal", value: "Portal"},
    {id: "keyword", value: "Keyword"},
    {id: "day", value: "Day"},
    {id: "month", value: "Month"},
    {id: "year", value: "Year"},
    {id: "hour", value: "Hour"},
    {id: "week", value: "Week"},
    {id: "campaign", value: "Campaign"},
    {id: "sold_count", value: "Sold Count"},
    {id: "score", value: "Score"},
    {id: "state", value: "State"},
    {id: "region", value: "Region"},
    {id: "new_school", value: "New School"},
    {id: "subject", value: "Subject"},
    {id: "program", value: "Program"},
    {id: "traffic_type", value: "Traffic Type"},
    {id: "age", value: "Age"},
    {id: "gender", value: "Gender"},
    {id: "degree_level", value: "Degree Seeking"},
    {id: "edu_level", value: "Edu Level"},
    {id: "creative", value: "Creative"},
    {id: "exclusive", value: "Exclusive"},
    {id: "cf", value: "Sold to CF"},
    {id: "handicap", value: "Handicap"},
    {id: "medium", value: "FL Medium"},
    {id: "work_hours", value: "Work Hours"},
    {id: "cw_agent", value: "CW Agent"},
    {id: "already", value: "Already Enrolled"},
    {id: "undecided", value: "Undecided"},
    {id: "undecided_bach", value: "Undecided to Bachelor"},
    {id: "portal_redirect", value: "Portal Redirect (SF)"},
    {id: "cert_upgrade", value: "Certificate Upgraded"},
    {id: "when_start_upgrade", value: "When Start Upgraded"},
    {id: "military", value: "Military"},
    {id: "working", value: "Working"},
    {id: "classes", value: "Classes"},
    {id: "company", value: "Company"},
].sort((a, b) => a.value.localeCompare(b.value));

const DATA_COLUMN_ORDER = yearBeforeMonthOrder(DATA_OPTIONS.map((d) => d.id));

const STAT_OPTIONS = [
    {id: "contact", value: "Contact", hidePercent: false},
    {id: "transfer", value: "Transfer", hidePercent: false},
    {id: "transfercontact", value: "Transfer Contact", hidePercent: false},
    {id: "app", value: "App", hidePercent: false},
    {id: "enroll", value: "Enroll", hidePercent: false},
    {id: "start", value: "Start", hidePercent: false},
    {id: "bad", value: "Bad", hidePercent: false},
    {id: "fraud", value: "Fraud", hidePercent: false},
    {id: "cpa", value: "CPA", hidePercent: true, isCurrency: true},
    {id: "cpe", value: "CPE", hidePercent: true, isCurrency: true},
    {id: "cps", value: "CPS", hidePercent: true, isCurrency: true},
    {id: "revenue", value: "Revenue", hidePercent: true, isCurrency: true},
];

const STAT_COLUMN_ORDER = STAT_OPTIONS.map((s) => s.id);

const FILTER_OPTIONS = [
    {id: "total", value: "Total"},
    {id: "broker", value: "Broker"},
    {id: "publisher", value: "Publisher"},
    {id: "school", value: "School"},
    {id: "contact", value: "Contact Rate"},
    {id: "transfer", value: "Transfer Rate"},
    {id: "hours", value: "Hours Old"},
    {id: "score", value: "Score"},
    {id: "when_start", value: "When Start"},
    {id: "edu_level", value: "Edu Level"},
    {id: "degree_level", value: "Degree Seeking"},
    {id: "campaigns", value: "Campaigns"},
    {id: "creative_ids", value: "Creative Urls"},
    {id: "keywords", value: "Keywords"},
    {id: "states", value: "States"},
    {id: "companies", value: "Companies"},
    {id: "traffic_types", value: "Traffic Types"},
    {id: "portals", value: "Portals"},
    {id: "new_school", value: "New School"},
    {id: "program", value: "Programs"},
    {id: "subject", value: "Subjects"},
    {id: "creative", value: "Creative"},
    {id: "creativeActive", value: "Creative Active"},
    {id: "exclusive", value: "Exclusive"},
    {id: "medium", value: "FL Medium"},
    {id: "cf", value: "Sold to CF"},
    {id: "age", value: "Age"},
    {id: "gender", value: "Gender"},
    {id: "conversion_type", value: "Conversion Type"},
    {id: "cw", value: "Sold to CW"},
    {id: "already", value: "Already Enrolled"},
    {id: "undecided", value: "Undecided"},
    {id: "undecided_bach", value: "Undecided to Bachelor"},
    {id: "portal_redirect", value: "Portal Redirect (SF)"},
    {id: "pixel", value: "Pixel Fired"},
    {id: "groups", value: "Groups"},
    {id: "cert_upgrade", value: "Certificate Upgraded"},
    {id: "when_start_upgrade", value: "When Start Upgraded"},
    {id: "military", value: "Military"},
    {id: "classes", value: "Classes"},
    {id: "working", value: "Working"},
].sort((a, b) => a.value.localeCompare(b.value));

const DEFAULT_DATA = [
    {id: "broker", value: "Broker"},
    {id: "publisher", value: "Publisher"},
];
const DEFAULT_STATS = [
    {id: "contact", value: "Contact"},
    {id: "transfer", value: "Transfer"},
    {id: "app", value: "App"},
    {id: "enroll", value: "Enroll"},
    {id: "fraud", value: "Fraud"},
];
const DEFAULT_FILTERS = [
    {id: "total", value: "Total"},
    {id: "broker", value: "Broker"},
];
const DEFAULT_BROKER_IDS = [172, 283];

const WHEN_START = [
    {id: 1, value: "None"},
    {id: 2, value: "Immediately"},
    {id: 4, value: "1-3 Months"},
    {id: 8, value: "4-6 Months"},
    {id: 16, value: "6+ Months"},
];
const WORKING = [
    {id: 1, value: "Yes, full-time"},
    {id: 2, value: "Yes, part-time"},
    {id: 0, value: "Not currently"},
];
const CLASSES = [
    {id: 0, value: "In a classroom"},
    {id: 1, value: "Online"},
    {id: 2, value: "Either way"},
];
const CONVERSION_TYPES = [
    {id: 0, value: "None"},
    {id: 1, value: "Fraud"},
    {id: 2, value: "Bad"},
    {id: 3, value: "Contact"},
    {id: 4, value: "Transfer"},
    {id: 5, value: "App"},
    {id: 6, value: "Enroll"},
    {id: 7, value: "Start"},
];
const EDU_LEVELS = [
    {id: 1, value: "High School"},
    {id: 2, value: "GED"},
    {id: 3, value: "Some College"},
    {id: 4, value: "Associates"},
    {id: 5, value: "Bachelors"},
    {id: 6, value: "Masters"},
    {id: 7, value: "Doctorate"},
];
const DEGREE_LEVELS = [
    {id: 1, value: "Certificate"},
    {id: 2, value: "Associates"},
    {id: 3, value: "Bachelors"},
    {id: 4, value: "Masters"},
    {id: 5, value: "Doctorate"},
];

const STAT_FIELD: Record<string, string> = {
    contact: "contacts",
    transfer: "transfers",
    transfercontact: "transferContact",
    app: "apps",
    enroll: "enrolls",
    start: "starts",
    bad: "bad",
    fraud: "fraud",
    cpa: "cpa",
    cpe: "cpe",
    cps: "cps",
    revenue: "revenue",
};

const HEAT_STAT_IDS = new Set(["contact", "transfer", "app"]);

const NUMERIC_DATA_COLUMNS = new Set([
    "sold_count", "day", "month", "year", "hour", "week", "age", "publisher_id", "score",
]);

function sortCellValue(row: any, column: string, columnValue: (row: any, key: string) => string) {
    if (column === "leads") return row.leads;
    if (STAT_FIELD[column]) return row[STAT_FIELD[column]];
    const display = columnValue(row, column);
    if (NUMERIC_DATA_COLUMNS.has(column)) {
        const asNum = Number(display);
        if (display !== "" && display != null && !isNaN(asNum)) return asNum;
        // e.g. month "01 - January"
        const lead = String(display).match(/^(\d+)/);
        if (lead) return Number(lead[1]);
    }
    return display;
}

function coerceSortNumber(v: any): number {
    if (typeof v === "number" && !isNaN(v)) return v;
    const asNum = Number(v);
    if (v !== "" && v != null && !isNaN(asNum)) return asNum;
    const lead = String(v ?? "").match(/^(\d+)/);
    if (lead) return Number(lead[1]);
    return 0;
}

function compareSortValues(av: any, bv: any, column: string) {
    const numericColumn = column === "leads" || !!STAT_FIELD[column] || NUMERIC_DATA_COLUMNS.has(column);
    if (numericColumn) {
        return coerceSortNumber(av) - coerceSortNumber(bv);
    }
    const aNum = Number(av);
    const bNum = Number(bv);
    if (av !== "" && bv !== "" && av != null && bv != null && !isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
    }
    return String(av ?? "").toLowerCase().localeCompare(String(bv ?? "").toLowerCase(), undefined, {numeric: true});
}

function delay(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
}

function formatElapsed(ms: number, showTenths = false) {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min > 0) return `${min}m ${sec}s`;
    if (showTenths) {
        const tenths = Math.floor((ms % 1000) / 100);
        return `${sec}.${tenths}s`;
    }
    return `${sec}s`;
}

function formatSlowRunDetails(
    elapsedMs: number,
    criteria: Record<string, any>,
    meta: {
        dataColumns: string[];
        statsColumns: string[];
        filterLabels: string[];
        presetName?: string;
        rowCount?: number;
    },
) {
    const lines: string[] = [
        `Elapsed: ${formatElapsed(elapsedMs)} (${elapsedMs} ms)`,
    ];
    if (meta.presetName) lines.push(`Preset: ${meta.presetName}`);
    if (meta.rowCount != null) lines.push(`Result rows: ${meta.rowCount}`);
    lines.push(
        "",
        `Date range: ${criteria.startDate} → ${criteria.endDate}`,
        `Unique leads: ${criteria.uniqueLeads}`,
        `Min results: ${criteria.minResults}`,
        "",
        `Data columns: ${meta.dataColumns.join(", ") || "(none)"}`,
        `Statistics: ${meta.statsColumns.join(", ") || "(none)"}`,
        `Active filters: ${meta.filterLabels.join(", ") || "(none)"}`,
        "",
        "Search criteria (API payload):",
        JSON.stringify(criteria, null, 2),
    );
    return lines.join("\n");
}

function filterSearch(all: any[], search: string, selected: any[]) {
    const s = selected.map((x: any) => x.id);
    const q = (search || "").toLowerCase();
    return all.filter((x: any) => !s.includes(x.id) && String(x.value).toLowerCase().includes(q));
}

const msSearch = (all: any[]) => (search: string, selected: any[]) => Promise.resolve(filterSearch(all, search, selected));

function splitTextList(text: string) {
    return text.split(/[,\n]+/).map((x) => x.trim()).filter(Boolean);
}

function splitNumericIds(text: string) {
    return text.split(/[,\n\s]+/).map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));
}

type HpPresetSnap = Partial<LinkoutPresetSnapshot & {
    selectedData?: any[];
    selectedStats?: any[];
    keywords?: string;
    scores?: string;
    portals?: string;
    groups?: string;
}>;

export default function ConversionStatsPage() {
    const auth = useAuth();
    const isAdmin = !!auth.user?.permissions.includes(PermissionType.Admin);

    React.useEffect(() => {
        document.title = "Conversion Stats | DN";
    }, []);

    const [dateRange, setDateRange] = React.useState([] as any);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [runStartedAt, setRunStartedAt] = React.useState(null as number | null);
    const [elapsedMs, setElapsedMs] = React.useState(null as number | null);
    const [lastRunDetails, setLastRunDetails] = React.useState(null as string | null);
    const [slowRunReporting, setSlowRunReporting] = React.useState(false);
    const [slowRunReported, setSlowRunReported] = React.useState(false);
    const [results, setResults] = React.useState([] as any[]);
    const [selectedData, setSelectedData] = React.useState(DEFAULT_DATA as any[]);
    const [selectedStats, setSelectedStats] = React.useState(DEFAULT_STATS as any[]);
    const [selectedFilterOpts, setSelectedFilterOpts] = React.useState(DEFAULT_FILTERS as any[]);
    const [uniqueLeads, setUniqueLeads] = React.useState(false);
    const [minResults, setMinResults] = React.useState("25");
    const [contactRate, setContactRate] = React.useState("0.01");
    const [transferRate, setTransferRate] = React.useState("0.01");
    const [hoursOldMin, setHoursOldMin] = React.useState("0");
    const [hoursOldMax, setHoursOldMax] = React.useState("72");
    const [ageMin, setAgeMin] = React.useState("18");
    const [ageMax, setAgeMax] = React.useState("99");
    const [campaignIds, setCampaignIds] = React.useState("");
    const [creativeUrls, setCreativeUrls] = React.useState("");
    const [states, setStates] = React.useState("");
    const [gender, setGender] = React.useState("");
    const [keywords, setKeywords] = React.useState("");
    const [scores, setScores] = React.useState("");
    const [portals, setPortals] = React.useState("");
    const [groups, setGroups] = React.useState("");
    const [boolFilters, setBoolFilters] = React.useState({} as Record<string, boolean>);

    const [brokers, setBrokers] = React.useState([] as any[]);
    const [companies, setCompanies] = React.useState([] as any[]);
    const [schools, setSchools] = React.useState([] as any[]);
    const [programs, setPrograms] = React.useState([] as any[]);
    const [subjects, setSubjects] = React.useState([] as any[]);
    const [trafficTypes, setTrafficTypes] = React.useState([] as any[]);
    const [selectedBrokers, setSelectedBrokers] = React.useState([] as any[]);
    const [selectedCompanies, setSelectedCompanies] = React.useState([] as any[]);
    const [selectedSchools, setSelectedSchools] = React.useState([] as any[]);
    const [selectedPrograms, setSelectedPrograms] = React.useState([] as any[]);
    const [selectedSubjects, setSelectedSubjects] = React.useState([] as any[]);
    const [selectedTraffic, setSelectedTraffic] = React.useState([] as any[]);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as any[]);
    const [publishersExclude, setPublishersExclude] = React.useState(false);
    const [selectedWhenStart, setSelectedWhenStart] = React.useState([] as any[]);
    const [selectedWorking, setSelectedWorking] = React.useState([] as any[]);
    const [selectedEdu, setSelectedEdu] = React.useState([] as any[]);
    const [selectedDegree, setSelectedDegree] = React.useState([] as any[]);
    const [selectedClasses, setSelectedClasses] = React.useState([] as any[]);
    const [selectedConvTypes, setSelectedConvTypes] = React.useState([] as any[]);

    const [presets, setPresets] = React.useState([] as any[]);
    const [selectedPreset, setSelectedPreset] = React.useState(null as {id: number; value: string} | null);
    const [saveOpen, setSaveOpen] = React.useState(false);
    const [saveName, setSaveName] = React.useState("");
    const [shareOpen, setShareOpen] = React.useState(false);
    const [shareUsers, setShareUsers] = React.useState([] as any[]);
    const [shareChecked, setShareChecked] = React.useState({} as Record<number, boolean>);
    const [sortStack, setSortStack] = React.useState<SortEntry[]>(DEFAULT_SORT_STACK);

    React.useEffect(() => {
        if (!loading || runStartedAt == null) return;
        const id = window.setInterval(() => {
            setElapsedMs(Date.now() - runStartedAt);
        }, 100);
        return () => window.clearInterval(id);
    }, [loading, runStartedAt]);

    const hasFilter = (id: string) => selectedFilterOpts.some((x) => x.id === id);

    const searchPresets = React.useCallback(async (search: string) => {
        const q = (search || "").toLowerCase();
        return presets
            .filter((p) => !q || String(p.name).toLowerCase().includes(q))
            .map((p) => ({id: p.id, value: p.name}));
    }, [presets]);

    const loadPresets = async () => {
        const response = await query(gql`
            query q($isLinkOut: Boolean!) {
                conversionStatPresets(isLinkOut: $isLinkOut) {
                    id repId name preset isLinkOut sharedRepIds
                }
            }
        `, {isLinkOut: false});
        setPresets(response?.conversionStatPresets || []);
    };

    React.useEffect(() => {
        const init = async () => {
            const d = RangeToTimes("Month to Date");
            setDateRange(d);
            const response = await query(gql`
                query q {
                    conversionStatsOptions {
                        brokers { id name }
                        companies { id name }
                        schools { id name }
                        programs { id name }
                        subjects { id name }
                        trafficTypes
                    }
                }
            `, {});
            const opts = response?.conversionStatsOptions;
            if (opts) {
                const b = opts.brokers.map((x: any) => ({id: x.id, value: x.name}));
                setBrokers(b);
                setSelectedBrokers(b.filter((x: any) => DEFAULT_BROKER_IDS.includes(x.id)));
                setCompanies(opts.companies.map((x: any) => ({id: x.name, value: x.name})));
                setSchools(opts.schools.map((x: any) => ({id: x.id, value: x.name})));
                setPrograms(opts.programs.map((x: any) => ({id: x.id, value: x.name})));
                setSubjects(opts.subjects.map((x: any) => ({id: x.id, value: x.name})));
                setTrafficTypes(opts.trafficTypes.map((t: string) => ({id: t, value: t})));
            }
            await loadPresets();
            setFiltersLoaded(true);
        };
        init().then(() => {});
    }, []);

    const columnValue = (row: any, key: string) => {
        const col = row.columns?.find((c: any) => c.dataType === key);
        return col ? col.value : "";
    };

    const buildCriteria = (snap?: HpPresetSnap) => {
        const date = snap?.dates ?? dateRange;
        const dataSel = snap?.data ?? snap?.selectedData ?? selectedData;
        const statsSel = snap?.selectedStats ?? selectedStats;
        const filterOpts = snap?.filterItems ?? selectedFilterOpts;
        const has = (id: string) => filterOpts.some((x: any) => x.id === id);
        const brokerSel = snap?.selectedBrokers ?? selectedBrokers;
        const schoolsSel = snap?.selectedSchools ?? selectedSchools;
        const companiesSel = snap?.selectedCompanies ?? selectedCompanies;
        const pubsSel = snap?.selectedPublishers ?? selectedPublishers;
        const pubsExclude = snap?.publishersExclude ?? publishersExclude;
        const programsSel = snap?.selectedPrograms ?? selectedPrograms;
        const subjectsSel = snap?.selectedSubjects ?? selectedSubjects;
        const whenStartSel = snap?.selectedWhenStart ?? selectedWhenStart;
        const workingSel = snap?.selectedWorking ?? selectedWorking;
        const eduSel = snap?.selectedEdu ?? selectedEdu;
        const degreeSel = snap?.selectedDegree ?? selectedDegree;
        const convSel = snap?.selectedConvTypes ?? selectedConvTypes;
        const classesSel = snap?.selectedClasses ?? selectedClasses;
        const trafficSel = snap?.selectedTraffic ?? selectedTraffic;
        const minRes = snap?.minResults ?? minResults;
        const contact = snap?.contactRate ?? contactRate;
        const transfer = snap?.transferRate ?? transferRate;
        const unique = snap?.uniqueLeads ?? uniqueLeads;
        const campaigns = snap?.campaignIds ?? campaignIds;
        const creatives = snap?.creativeUrls ?? creativeUrls;
        const statesVal = snap?.states ?? states;
        const genderVal = snap?.gender ?? gender;
        const hoursMin = snap?.hoursOldMin ?? hoursOldMin;
        const hoursMax = snap?.hoursOldMax ?? hoursOldMax;
        const ageLo = snap?.ageMin ?? ageMin;
        const ageHi = snap?.ageMax ?? ageMax;
        const keywordsVal = snap?.keywords ?? keywords;
        const scoresVal = snap?.scores ?? scores;
        const portalsVal = snap?.portals ?? portals;
        const groupsVal = snap?.groups ?? groups;
        const bools = snap?.boolFilters ?? boolFilters;

        const criteria: any = {
            dataTypes: dataSel.map((x: any) => x.id),
            startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
            endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
            uniqueLeads: unique,
            minResults: has("total") ? (parseInt(minRes, 10) || 0) : 0,
        };
        if (has("broker") && brokerSel.length) criteria.brokerIds = toIntIds(brokerSel);
        if (has("school") && schoolsSel.length) criteria.schoolIds = toIntIds(schoolsSel);
        if (has("companies") && companiesSel.length) criteria.companyCodes = companiesSel.map((x: any) => x.id);
        if (has("publisher") && pubsSel.length) {
            if (pubsExclude) criteria.publisherIdsExclude = toIntIds(pubsSel);
            else criteria.publisherIds = toIntIds(pubsSel);
        }
        if (has("program") && programsSel.length) criteria.programIds = toIntIds(programsSel);
        if (has("subject") && subjectsSel.length) criteria.subjectIds = toIntIds(subjectsSel);
        if (has("when_start") && whenStartSel.length) criteria.whenStartIds = toIntIds(whenStartSel);
        if (has("working") && workingSel.length) criteria.workingIds = toIntIds(workingSel);
        if (has("edu_level") && eduSel.length) criteria.eduLevelIds = toIntIds(eduSel);
        if (has("degree_level") && degreeSel.length) criteria.degreeLevelIds = toIntIds(degreeSel);
        if (has("conversion_type") && convSel.length) criteria.conversionTypeIds = toIntIds(convSel);
        if (has("classes") && classesSel.length) criteria.classesIds = toIntIds(classesSel);
        if (has("traffic_types") && trafficSel.length) criteria.trafficTypes = trafficSel.map((x: any) => String(x.id));
        if (has("campaigns") && campaigns.trim()) {
            criteria.campaignIds = campaigns.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));
        }
        if (has("states") && statesVal.trim()) {
            criteria.states = statesVal.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
        }
        if (has("creative_ids") && creatives.trim()) {
            criteria.creativeUrls = creatives.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
        }
        if (has("keywords") && keywordsVal.trim()) {
            criteria.keywordValues = splitTextList(keywordsVal);
        }
        if (has("score") && scoresVal.trim()) {
            criteria.scores = splitTextList(scoresVal);
        }
        if (has("portals") && portalsVal.trim()) {
            criteria.portalIds = splitNumericIds(portalsVal);
        }
        if (has("groups") && groupsVal.trim()) {
            criteria.groupCapIds = splitNumericIds(groupsVal);
        }
        if (has("hours")) {
            criteria.hoursOldMin = parseInt(hoursMin, 10);
            criteria.hoursOldMax = parseInt(hoursMax, 10);
        }
        if (has("age")) {
            criteria.ageMin = parseInt(ageLo, 10);
            criteria.ageMax = parseInt(ageHi, 10);
        }
        if (has("contact")) criteria.contactRate = parseFloat(contact);
        if (has("transfer")) criteria.transferRate = parseFloat(transfer);
        if (has("gender") && genderVal) criteria.gender = genderVal;
        if (has("pixel")) criteria.pixel = bools.pixel !== false;
        const boolMap: Record<string, string> = {
            new_school: "newSchool", creative: "creative", exclusive: "exclusive", medium: "medium",
            cf: "cf", cw: "cw", already: "already", undecided_bach: "undecidedBach",
            cert_upgrade: "certUpgrade", when_start_upgrade: "whenStartUpgrade", military: "military",
            portal_redirect: "portalRedirect", undecided: "undecided", creativeActive: "creativeActive",
        };
        Object.keys(boolMap).forEach((k) => {
            if (has(k) && bools[k] != null) criteria[boolMap[k]] = !!bools[k];
        });
        return {criteria, dataSel, statsSel};
    };

    const reload = async (snap?: HpPresetSnap) => {
        const {criteria, dataSel, statsSel} = buildCriteria(snap);
        if (!dataSel.length) {
            toast.error("Select at least one data column");
            return;
        }
        if (!statsSel.length) {
            toast.error("Select at least one statistic");
            return;
        }
        const started = Date.now();
        setRunStartedAt(started);
        setElapsedMs(0);
        setSlowRunReported(false);
        setLoading(true);
        const runMeta = {
            dataColumns: dataSel.map((x: any) => x.value),
            statsColumns: statsSel.map((x: any) => x.value),
            filterLabels: (snap?.filterItems ?? selectedFilterOpts).map((x: any) => x.value),
            presetName: selectedPreset?.value,
        };
        let rowCount: number | undefined;
        try {
            const searchCriteria = criteria;
            const req = await query(gql`
                query q($searchCriteria: ConversionStatsSearchCriteria!) {
                    conversionStatsAsyncRequest(searchCriteria: $searchCriteria)
                }
            `, {searchCriteria});
            const id = req?.conversionStatsAsyncRequest;
            if (!id) {
                toast.error("Unable to start report");
                return;
            }
            while (true) {
                const poll = await query(gql`
                    query q($id: String!) {
                        conversionStatsAsyncGet(id: $id) {
                            columns { dataType value }
                            leads contacts transfers transferContact apps enrolls starts bad fraud
                            revenue cpa cpe cps publisherId
                        }
                    }
                `, {id});
                if (poll?.conversionStatsAsyncGet) {
                    rowCount = poll.conversionStatsAsyncGet.length;
                    setResults(poll.conversionStatsAsyncGet);
                    setSortStack(DEFAULT_SORT_STACK);
                    break;
                }
                await delay(2000);
            }
        } catch (e: any) {
            toast.error(e?.message || "Report failed");
        } finally {
            const elapsed = Date.now() - started;
            setElapsedMs(elapsed);
            setLastRunDetails(formatSlowRunDetails(elapsed, criteria, {...runMeta, rowCount}));
            setRunStartedAt(null);
            setLoading(false);
        }
    };

    const reportSlowRun = async () => {
        if (!lastRunDetails || elapsedMs == null) return;
        setSlowRunReporting(true);
        try {
            await mutate(gql`
                mutation m($elapsedMs: Int!, $details: String!) {
                    reportSlowConversionStats(elapsedMs: $elapsedMs, details: $details)
                }
            `, {elapsedMs, details: lastRunDetails});
            setSlowRunReported(true);
            toast.success("Slow run reported — email sent");
        } catch (e: any) {
            toast.error(e?.message || "Failed to send report");
        }
        setSlowRunReporting(false);
    };

    const sortedResults = React.useMemo(() => {
        return applyMultiSort(results, sortStack, (a, b, column) => {
            const av = sortCellValue(a, column, columnValue);
            const bv = sortCellValue(b, column, columnValue);
            return compareSortValues(av, bv, column);
        });
    }, [results, sortStack]);

    const displayData = React.useMemo(
        () => uniqueOrderedByIds(selectedData, DATA_COLUMN_ORDER),
        [selectedData],
    );
    const displayStats = React.useMemo(
        () => uniqueOrderedByIds(selectedStats, STAT_COLUMN_ORDER),
        [selectedStats],
    );

    const totals = React.useMemo(() => {
        const t: any = {
            leads: 0, contacts: 0, transfers: 0, transferContact: 0,
            apps: 0, enrolls: 0, starts: 0, bad: 0, fraud: 0, revenue: 0,
        };
        results.forEach((r) => {
            t.leads += r.leads;
            t.contacts += r.contacts;
            t.transfers += r.transfers;
            t.transferContact += r.transferContact;
            t.apps += r.apps;
            t.enrolls += r.enrolls;
            t.starts += r.starts;
            t.bad += r.bad;
            t.fraud += r.fraud;
            t.revenue += r.revenue;
        });
        t.cpa = t.apps ? t.revenue / t.apps : 0;
        t.cpe = t.enrolls ? t.revenue / t.enrolls : 0;
        t.cps = t.starts ? t.revenue / t.starts : 0;
        return t;
    }, [results]);

    const singleBrokerHighlight = hasFilter("broker") && selectedBrokers.length === 1;

    const fmtInt = (n: number) => Number(n || 0).toLocaleString(undefined, {maximumFractionDigits: 0});
    const fmtCurrency = (n: number) => "$" + Number(n || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const fmtStat = (val: number, isCurrency?: boolean) => (isCurrency ? fmtCurrency(val) : fmtInt(val));

    const pct = (n: number, d: number) => d ? ((n * 100) / d).toFixed(1) + "%" : "";
    const statPct = (val: number, total: number) => pct(val, total);
    const statRate = (val: number, total: number) => total ? (val * 100) / total : 0;

    const heatColor = (statId: string, val: number, rowLeads: number): string | undefined => {
        if (!singleBrokerHighlight || !HEAT_STAT_IDS.has(statId) || rowLeads < 20 || !totals.leads) return undefined;
        const field = STAT_FIELD[statId];
        const rate = statRate(val, rowLeads);
        const avg = (totals[field] / totals.leads) * 100;
        if (rate < avg * 0.6) return "red";
        if (rate >= avg * 1.4) return "green";
        return undefined;
    };

    const download = () => {
        const sample = expandExportDataColumns(displayData, columnValue, sortedResults[0] || {});
        const headers: string[] = [
            ...sample.headers,
            "Total",
        ];
        displayStats.forEach((s) => {
            headers.push(s.value);
            if (!STAT_OPTIONS.find((o) => o.id === s.id)?.hidePercent) {
                headers.push(s.value + " %");
            }
        });
        const rows = sortedResults.map((r) => {
            const {values} = expandExportDataColumns(displayData, columnValue, r);
            const line: any[] = [...values];
            line.push(fmtInt(r.leads));
            displayStats.forEach((s) => {
                const field = STAT_FIELD[s.id];
                const opt = STAT_OPTIONS.find((o) => o.id === s.id);
                const v = r[field];
                line.push(opt?.isCurrency
                    ? Number(v || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                    : fmtInt(v));
                if (!opt?.hidePercent) {
                    line.push(statPct(r[field], r.leads));
                }
            });
            return line;
        });
        downloadCsv("conversionstats.csv", [headers, ...rows]);
    };

    const copyTable = async () => {
        const headers = [
            ...displayData.map((d) => d.value),
            "Total",
            ...displayStats.map((s) => s.value),
        ];
        const body = sortedResults.map((r) => {
            const line: string[] = displayData.map((d) => String(columnValue(r, d.id) ?? ""));
            line.push(fmtInt(r.leads));
            displayStats.forEach((s) => {
                const field = STAT_FIELD[s.id];
                const opt = STAT_OPTIONS.find((o) => o.id === s.id);
                const val = r[field];
                let cell = fmtStat(val, opt?.isCurrency);
                if (!opt?.hidePercent) cell += ` (${statPct(val, r.leads)})`;
                line.push(cell);
            });
            return line;
        });
        const totalLine: string[] = displayData.map((_, i) => (i === 0 ? "Total" : ""));
        totalLine.push(fmtInt(totals.leads));
        displayStats.forEach((s) => {
            const field = STAT_FIELD[s.id];
            const opt = STAT_OPTIONS.find((o) => o.id === s.id);
            const val = totals[field];
            let cell = fmtStat(val, opt?.isCurrency);
            if (!opt?.hidePercent) cell += ` (${statPct(val, totals.leads)})`;
            totalLine.push(cell);
        });
        const tsv = [headers, ...body, totalLine].map((row) => row.join("\t")).join("\n");
        try {
            await navigator.clipboard.writeText(tsv);
            toast.success("Copied table (headers + data)");
        } catch {
            toast.error("Unable to copy to clipboard");
        }
    };

    const currentPresetPayload = () => ({
        filters: {
            total: hasFilter("total") ? minResults : undefined,
            contactRate: hasFilter("contact") ? contactRate : undefined,
            transferRate: hasFilter("transfer") ? transferRate : undefined,
        },
        dates: dateRange.map((d: any) => moment(d).toISOString()),
        data: selectedData.map((x) => x.id),
        stats: selectedStats.map((x) => x.id),
        filterItems: selectedFilterOpts.map((x) => x.id),
        uniqueLeads,
        ...sortStackForPreset(sortStack),
        isLinkOut: false,
        selectedBrokers: selectedBrokers.map((x) => x.id),
        selectedPublishers: selectedPublishers.map((x) => x.id),
        publishersExclude,
        selectedSchools: selectedSchools.map((x) => x.id),
        selectedCompanies: selectedCompanies.map((x) => x.id),
        boolFilters,
        campaignIds, creativeUrls, states, gender,
        keywords, scores, portals, groups,
        hoursOldMin, hoursOldMax, ageMin, ageMax,
        selectedWhenStart: selectedWhenStart.map((x) => x.id),
        selectedWorking: selectedWorking.map((x) => x.id),
        selectedEdu: selectedEdu.map((x) => x.id),
        selectedDegree: selectedDegree.map((x) => x.id),
        selectedClasses: selectedClasses.map((x) => x.id),
        selectedConvTypes: selectedConvTypes.map((x) => x.id),
        selectedPrograms: selectedPrograms.map((x) => x.id),
        selectedSubjects: selectedSubjects.map((x) => x.id),
        selectedTraffic: selectedTraffic.map((x) => x.id),
        minResults,
        contactRate, transferRate,
    });

    const savePreset = async () => {
        if (!saveName.trim()) {
            toast.error("Enter a preset name");
            return;
        }
        await mutate(gql`
            mutation m($input: SaveConversionStatPresetInput!) {
                saveConversionStatPreset(input: $input) { id }
            }
        `, {input: {name: saveName.trim(), isLinkOut: false, preset: JSON.stringify(currentPresetPayload())}});
        toast.success("Preset saved");
        setSaveOpen(false);
        setSaveName("");
        await loadPresets();
    };

    const loadPreset = async () => {
        const p = presets.find((x) => x.id === selectedPreset?.id);
        if (!p) return;
        const raw = JSON.parse(p.preset);
        const snap = normalizeConversionStatPreset(raw, {
            dataOptions: DATA_OPTIONS,
            statOptions: STAT_OPTIONS,
            filterOptions: FILTER_OPTIONS,
            brokers,
            schools,
            companies,
            programs,
            subjects,
            trafficTypes,
            whenStart: WHEN_START,
            working: WORKING,
            eduLevels: EDU_LEVELS,
            degreeLevels: DEGREE_LEVELS,
            classes: CLASSES,
            conversionTypes: CONVERSION_TYPES,
        });
        const keywordsVal = String(raw.keywords ?? raw.filters?.keywords ?? "");
        const scoresVal = String(raw.scores ?? raw.filters?.scores ?? "");
        const portalsVal = Array.isArray(raw.portals)
            ? raw.portals.join(",")
            : Array.isArray(raw.filters?.portals)
                ? raw.filters.portals.join(",")
                : String(raw.portals ?? raw.filters?.portals ?? "");
        const groupsVal = Array.isArray(raw.groups)
            ? raw.groups.join(",")
            : Array.isArray(raw.filters?.groups)
                ? raw.filters.groups.join(",")
                : String(raw.groups ?? raw.filters?.groups ?? "");
        const bools = {...snap.boolFilters};
        ["portal_redirect", "undecided", "creativeActive", "pixel"].forEach((k) => {
            if (raw.boolFilters?.[k] != null) bools[k] = !!raw.boolFilters[k];
            else if (raw.filters?.[k] != null) bools[k] = !!raw.filters[k];
        });
        setDateRange(snap.dates);
        setSelectedData(snap.data);
        setSelectedStats(snap.stats);
        setSelectedFilterOpts(snap.filterItems);
        setUniqueLeads(raw.uniqueLeads != null ? !!raw.uniqueLeads : false);
        setMinResults(snap.minResults);
        setContactRate(snap.contactRate);
        setTransferRate(snap.transferRate);
        setSelectedBrokers(snap.selectedBrokers);
        setSelectedPublishers(snap.selectedPublishers);
        setPublishersExclude(snap.publishersExclude);
        setSelectedSchools(snap.selectedSchools);
        setSelectedCompanies(snap.selectedCompanies);
        setSelectedPrograms(snap.selectedPrograms);
        setSelectedSubjects(snap.selectedSubjects);
        setSelectedTraffic(snap.selectedTraffic);
        setSelectedWhenStart(snap.selectedWhenStart);
        setSelectedWorking(snap.selectedWorking);
        setSelectedEdu(snap.selectedEdu);
        setSelectedDegree(snap.selectedDegree);
        setSelectedClasses(snap.selectedClasses);
        setSelectedConvTypes(snap.selectedConvTypes);
        setBoolFilters(bools);
        setCampaignIds(snap.campaignIds);
        setCreativeUrls(snap.creativeUrls);
        setStates(snap.states);
        setGender(snap.gender);
        setKeywords(keywordsVal);
        setScores(scoresVal);
        setPortals(portalsVal);
        setGroups(groupsVal);
        setHoursOldMin(snap.hoursOldMin);
        setHoursOldMax(snap.hoursOldMax);
        setAgeMin(snap.ageMin);
        setAgeMax(snap.ageMax);
        setSortStack(parseSortStack(raw, DEFAULT_SORT_STACK));
        toast.info("Preset loaded — running report");
        await reload({
            ...snap,
            selectedStats: snap.stats,
            boolFilters: bools,
            keywords: keywordsVal,
            scores: scoresVal,
            portals: portalsVal,
            groups: groupsVal,
        });
    };

    const deletePreset = async () => {
        if (!selectedPreset?.id) return;
        if (!window.confirm("Delete this preset?")) return;
        await mutate(gql`mutation m($id: Int!) { removeConversionStatPreset(id: $id) }`, {id: selectedPreset.id});
        toast.success("Preset deleted");
        setSelectedPreset(null);
        await loadPresets();
    };

    const openShare = async () => {
        const p = presets.find((x) => x.id === selectedPreset?.id);
        if (!p || p.repId !== auth.user?.id) {
            toast.error("You can only share presets you own");
            return;
        }
        const response = await query(gql`
            query q($input: UsersInput!) {
                users(input: $input) { items { id name } }
            }
        `, {input: {active: true, search: "", skip: 0, take: 500, sort: [{column: "name", isAsc: true}]}});
        const users = (response?.users?.items || [])
            .filter((u: any) => u.id !== auth.user?.id)
            .map((u: any) => ({id: u.id, name: u.name || String(u.id)}));
        setShareUsers(users);
        const checked: Record<number, boolean> = {};
        (p.sharedRepIds || []).forEach((id: number) => { checked[id] = true; });
        setShareChecked(checked);
        setShareOpen(true);
    };

    const saveShare = async () => {
        const sharedRepIds = Object.keys(shareChecked).filter((k) => shareChecked[Number(k)]).map((k) => Number(k));
        await mutate(gql`
            mutation m($presetId: Int!, $sharedRepIds: [Int!]!) {
                shareConversionStatPreset(presetId: $presetId, sharedRepIds: $sharedRepIds)
            }
        `, {presetId: selectedPreset!.id, sharedRepIds});
        toast.success("Preset shared");
        setShareOpen(false);
        await loadPresets();
    };

    const boolToggle = (key: string) => (
        <FormControlLabel
            control={<Switch checked={!!boolFilters[key]} onChange={(e) => setBoolFilters({...boolFilters, [key]: e.target.checked})} />}
            label={FILTER_OPTIONS.find((f) => f.id === key)?.value || key}
        />
    );

    if (!filtersLoaded) return <Loading />;

    const selectedPresetRow = presets.find((x) => x.id === selectedPreset?.id);

    return (
        <Box>
            <h2>Conversion Stats</h2>
            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                <Box>
                    Date Range<br />
                    <DateRange showTime={true} dateRange={dateRange} setDateRange={(d: any) => setDateRange(d)} style={{marginRight: "10px"}} />
                </Box>
                <Box minWidth={160}>
                    Data<br />
                    <MultiSelect searchFunction={msSearch(DATA_OPTIONS)}
                                 selectedItems={selectedData}
                                 setSelectedItems={(items: any[]) => setSelectedData(uniqueOrderedByIds(items, DATA_COLUMN_ORDER))}
                                 placeholder="Data" />
                </Box>
                <Box minWidth={160}>
                    Statistics<br />
                    <MultiSelect searchFunction={msSearch(STAT_OPTIONS.map((x) => ({id: x.id, value: x.value})))}
                                 selectedItems={selectedStats}
                                 setSelectedItems={(items: any[]) => setSelectedStats(uniqueOrderedByIds(items, STAT_COLUMN_ORDER))}
                                 placeholder="Stats" />
                </Box>
                <Box minWidth={160}>
                    Filters<br />
                    <MultiSelect searchFunction={msSearch(FILTER_OPTIONS)}
                                 selectedItems={selectedFilterOpts} setSelectedItems={setSelectedFilterOpts} placeholder="Filters" />
                </Box>
                <Box>
                    Unique Leads<br />
                    <Switch checked={uniqueLeads} onChange={(e) => setUniqueLeads(e.target.checked)} />
                </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                {hasFilter("total") && (
                    <Box>Total &gt;=<br /><TextField size="small" value={minResults} onChange={(e) => setMinResults(e.target.value)} sx={{width: 80}} /></Box>
                )}
                {hasFilter("contact") && (
                    <Box>Contact Rate % &gt;=<br /><TextField size="small" value={contactRate} onChange={(e) => setContactRate(e.target.value)} sx={{width: 80}} /></Box>
                )}
                {hasFilter("transfer") && (
                    <Box>Transfer Rate % &gt;=<br /><TextField size="small" value={transferRate} onChange={(e) => setTransferRate(e.target.value)} sx={{width: 80}} /></Box>
                )}
                {hasFilter("broker") && (
                    <Box minWidth={180}>Brokers<br />
                        <MultiSelect searchFunction={msSearch(brokers)}
                                     selectedItems={selectedBrokers} setSelectedItems={setSelectedBrokers} placeholder="Brokers" />
                    </Box>
                )}
                {hasFilter("publisher") && (
                    <Box minWidth={200}>Publishers<br />
                        <MultiSelect searchFunction={searchPubs} selectedItems={selectedPublishers} setSelectedItems={setSelectedPublishers} placeholder="Publishers" />
                        <FormControlLabel control={<Checkbox checked={publishersExclude} onChange={(e) => setPublishersExclude(e.target.checked)} />} label="Exclude" />
                    </Box>
                )}
                {hasFilter("school") && (
                    <Box minWidth={180}>Schools<br />
                        <MultiSelect searchFunction={msSearch(schools)}
                                     selectedItems={selectedSchools} setSelectedItems={setSelectedSchools} placeholder="Schools" />
                    </Box>
                )}
                {hasFilter("companies") && (
                    <Box minWidth={180}>Companies<br />
                        <MultiSelect searchFunction={msSearch(companies)}
                                     selectedItems={selectedCompanies} setSelectedItems={setSelectedCompanies} placeholder="Companies" />
                    </Box>
                )}
                {hasFilter("program") && (
                    <Box minWidth={180}>Programs<br />
                        <MultiSelect searchFunction={msSearch(programs)}
                                     selectedItems={selectedPrograms} setSelectedItems={setSelectedPrograms} placeholder="Programs" />
                    </Box>
                )}
                {hasFilter("subject") && (
                    <Box minWidth={180}>Subjects<br />
                        <MultiSelect searchFunction={msSearch(subjects)}
                                     selectedItems={selectedSubjects} setSelectedItems={setSelectedSubjects} placeholder="Subjects" />
                    </Box>
                )}
                {hasFilter("traffic_types") && (
                    <Box minWidth={160}>Traffic Types<br />
                        <MultiSelect searchFunction={msSearch(trafficTypes)}
                                     selectedItems={selectedTraffic} setSelectedItems={setSelectedTraffic} placeholder="Traffic" />
                    </Box>
                )}
                {hasFilter("when_start") && (
                    <Box minWidth={160}>When Start<br />
                        <MultiSelect searchFunction={msSearch(WHEN_START)}
                                     selectedItems={selectedWhenStart} setSelectedItems={setSelectedWhenStart} placeholder="When Start" />
                    </Box>
                )}
                {hasFilter("working") && (
                    <Box minWidth={160}>Working<br />
                        <MultiSelect searchFunction={msSearch(WORKING)}
                                     selectedItems={selectedWorking} setSelectedItems={setSelectedWorking} placeholder="Working" />
                    </Box>
                )}
                {hasFilter("edu_level") && (
                    <Box minWidth={160}>Edu Levels<br />
                        <MultiSelect searchFunction={msSearch(EDU_LEVELS)}
                                     selectedItems={selectedEdu} setSelectedItems={setSelectedEdu} placeholder="Edu" />
                    </Box>
                )}
                {hasFilter("degree_level") && (
                    <Box minWidth={160}>Degree Seeking<br />
                        <MultiSelect searchFunction={msSearch(DEGREE_LEVELS)}
                                     selectedItems={selectedDegree} setSelectedItems={setSelectedDegree} placeholder="Degree" />
                    </Box>
                )}
                {hasFilter("classes") && (
                    <Box minWidth={160}>Classes<br />
                        <MultiSelect searchFunction={msSearch(CLASSES)}
                                     selectedItems={selectedClasses} setSelectedItems={setSelectedClasses} placeholder="Classes" />
                    </Box>
                )}
                {hasFilter("conversion_type") && (
                    <Box minWidth={160}>Conversion Type<br />
                        <MultiSelect searchFunction={msSearch(CONVERSION_TYPES)}
                                     selectedItems={selectedConvTypes} setSelectedItems={setSelectedConvTypes} placeholder="Type" />
                    </Box>
                )}
                {hasFilter("hours") && (
                    <Box>Hours Old<br />
                        <TextField size="small" value={hoursOldMin} onChange={(e) => setHoursOldMin(e.target.value)} sx={{width: 70}} /> to{" "}
                        <TextField size="small" value={hoursOldMax} onChange={(e) => setHoursOldMax(e.target.value)} sx={{width: 70}} />
                    </Box>
                )}
                {hasFilter("age") && (
                    <Box>Age<br />
                        <TextField size="small" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} sx={{width: 70}} /> to{" "}
                        <TextField size="small" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} sx={{width: 70}} />
                    </Box>
                )}
                {hasFilter("campaigns") && (
                    <Box>Campaign IDs<br /><TextField size="small" value={campaignIds} onChange={(e) => setCampaignIds(e.target.value)} sx={{width: 180}} placeholder="1,2,3" /></Box>
                )}
                {hasFilter("creative_ids") && (
                    <Box>Creative URLs<br /><TextField size="small" value={creativeUrls} onChange={(e) => setCreativeUrls(e.target.value)} sx={{width: 180}} /></Box>
                )}
                {hasFilter("states") && (
                    <Box>States<br /><TextField size="small" value={states} onChange={(e) => setStates(e.target.value)} sx={{width: 120}} placeholder="CA,NY" /></Box>
                )}
                {hasFilter("keywords") && (
                    <Box>Keywords<br />
                        <TextField
                            size="small"
                            multiline
                            rows={3}
                            value={keywords}
                            onChange={(e) => setKeywords(e.target.value)}
                            sx={{width: 200, "& textarea": {resize: "none", overflowY: "auto"}}}
                        />
                    </Box>
                )}
                {hasFilter("score") && (
                    <Box>Scores<br />
                        <TextField size="small" value={scores} onChange={(e) => setScores(e.target.value)} sx={{width: 160}} placeholder="1,2,3" />
                    </Box>
                )}
                {hasFilter("portals") && (
                    <Box>Portals (TCPA IDs)<br />
                        <TextField size="small" value={portals} onChange={(e) => setPortals(e.target.value)} sx={{width: 180}} placeholder="1,2,3" />
                    </Box>
                )}
                {hasFilter("groups") && (
                    <Box>Groups (Cap IDs)<br />
                        <TextField size="small" value={groups} onChange={(e) => setGroups(e.target.value)} sx={{width: 180}} placeholder="1,2,3" />
                    </Box>
                )}
                {hasFilter("gender") && (
                    <Box>Gender<br />
                        <TextField select size="small" value={gender} onChange={(e) => setGender(e.target.value)} sx={{width: 100}}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="M">Male</MenuItem>
                            <MenuItem value="F">Female</MenuItem>
                        </TextField>
                    </Box>
                )}
                {["new_school", "creative", "exclusive", "medium", "cf", "cw", "already", "undecided_bach", "cert_upgrade", "when_start_upgrade", "military", "portal_redirect", "undecided", "creativeActive"]
                    .filter(hasFilter).map((k) => <Box key={k}>{boolToggle(k)}</Box>)}
                {hasFilter("pixel") && <Box>{boolToggle("pixel")}</Box>}
            </Box>

            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={2}>
                <Button variant="contained" onClick={() => reload()} disabled={loading}>Run Report</Button>
                {(loading || elapsedMs != null) && (
                    <Box sx={{color: "text.secondary", fontSize: 14, minWidth: 100}}>
                        {loading ? "Running: " : "Last run: "}
                        {formatElapsed(elapsedMs ?? 0, loading)}
                    </Box>
                )}
                {!loading && elapsedMs != null && elapsedMs > 30000 && lastRunDetails && (
                    <Tooltip title="Email run parameters and timing to engineering so we can investigate slow queries">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={reportSlowRun}
                                disabled={slowRunReporting || slowRunReported}
                            >
                                {slowRunReported ? "Reported" : slowRunReporting ? "Sending…" : "Report Slow Run"}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Box minWidth={240}>
                    <SingleSelect
                        key={presets.length}
                        searchFunction={searchPresets}
                        selectedItem={selectedPreset}
                        setSelectedItem={setSelectedPreset}
                        placeholder="Search presets"
                    />
                </Box>
                <Button variant="outlined" onClick={loadPreset} disabled={!selectedPreset?.id}>Go</Button>
                <IconButton onClick={deletePreset} disabled={!selectedPreset?.id} title="Delete preset"><DeleteIcon /></IconButton>
                <Button variant="outlined" onClick={() => setSaveOpen(true)}>Save Preset</Button>
                {isAdmin && selectedPresetRow && selectedPresetRow.repId === auth.user?.id && (
                    <IconButton onClick={openShare} title="Share preset"><ShareIcon /></IconButton>
                )}
                <Tooltip title="Copy table (headers + data)">
                    <span>
                        <IconButton onClick={copyTable} disabled={!results.length}><ContentCopyIcon /></IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Download CSV">
                    <span>
                        <IconButton onClick={download} disabled={!results.length}><FileDownloadIcon /></IconButton>
                    </span>
                </Tooltip>
            </Box>

            {loading && <Loading />}
            {!loading && results.length === 0 && <Alert severity="info">No results — run a report.</Alert>}
            {!loading && results.length > 0 && (
                <>
                {sortStack.length > 0 && (
                    <Box sx={{display: "flex", justifyContent: "flex-end", mb: 0.5}}>
                        <Button size="small" variant="text" onClick={() => setSortStack([])}>
                            Clear sort
                        </Button>
                    </Box>
                )}
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableHeaderRow>
                                {displayData.map((c) => (
                                    <TableCell key={c.id}>
                                        <ConversionStatSortLabel
                                            column={c.id}
                                            label={c.value}
                                            sortStack={sortStack}
                                            onSortStackChange={setSortStack}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    <ConversionStatSortLabel
                                        column="leads"
                                        label="Total"
                                        sortStack={sortStack}
                                        onSortStackChange={setSortStack}
                                        align="right"
                                    />
                                </TableCell>
                                {displayStats.map((s) => (
                                    <TableCell key={s.id} align="right">
                                        <ConversionStatSortLabel
                                            column={s.id}
                                            label={s.value}
                                            sortStack={sortStack}
                                            onSortStackChange={setSortStack}
                                            align="right"
                                        />
                                    </TableCell>
                                ))}
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {sortedResults.map((row, idx) => (
                                <StripedTableRow key={idx}>
                                    {displayData.map((c) => (
                                        <TableCell key={c.id}>
                                            {c.id === "publisher" && row.publisherId ? (
                                                <MuiLink component={Link} to={`/publishers/${row.publisherId}`}>{columnValue(row, c.id)}</MuiLink>
                                            ) : columnValue(row, c.id)}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">{fmtInt(row.leads)}</TableCell>
                                    {displayStats.map((s) => {
                                        const field = STAT_FIELD[s.id];
                                        const opt = STAT_OPTIONS.find((o) => o.id === s.id);
                                        const val = row[field];
                                        const color = heatColor(s.id, val, row.leads);
                                        return (
                                            <TableCell key={s.id} align="right" sx={color ? {color} : undefined}>
                                                {fmtStat(val, opt?.isCurrency)}
                                                {!opt?.hidePercent ? ` (${statPct(val, row.leads)})` : ""}
                                            </TableCell>
                                        );
                                    })}
                                </StripedTableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableFooterRow>
                                {displayData.map((c, i) => <TableCell key={c.id}>{i === 0 ? "Total" : ""}</TableCell>)}
                                <TableCell align="right">{fmtInt(totals.leads)}</TableCell>
                                {displayStats.map((s) => {
                                    const field = STAT_FIELD[s.id];
                                    const opt = STAT_OPTIONS.find((o) => o.id === s.id);
                                    const val = totals[field];
                                    return (
                                        <TableCell key={s.id} align="right">
                                            {fmtStat(val, opt?.isCurrency)}
                                            {!opt?.hidePercent ? ` (${statPct(val, totals.leads)})` : ""}
                                        </TableCell>
                                    );
                                })}
                            </TableFooterRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
                </>
            )}

            <Dialog open={saveOpen} onClose={() => setSaveOpen(false)}>
                <DialogTitle>Save Preset</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth label="Name" value={saveName} onChange={(e) => setSaveName(e.target.value)} sx={{mt: 1}} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={savePreset}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Share Preset</DialogTitle>
                <DialogContent>
                    {shareUsers.map((u) => (
                        <FormControlLabel key={u.id}
                            control={<Checkbox checked={!!shareChecked[u.id]} onChange={(e) => setShareChecked({...shareChecked, [u.id]: e.target.checked})} />}
                            label={u.name}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveShare}>Share</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
